import { useState } from "react";
import { Create, SimpleForm, TextInput, useNotify, useRedirect } from "react-admin";
import { CircularProgress } from "@material-ui/core";
import { useFormContext } from "react-hook-form";
import { ConfirmationModal } from "../../components";
import sendLuftExport, { checkLuftResend } from "./functions/sendLuftExport";
import * as S from "./index.styles";

const normalizeCycleId = (value) => {
  if (!value) {
    return "";
  }

  return String(value).replace(/\D/g, "").slice(0, 9);
};

const CustomToolbar = ({ loading, onSend, ...props }) => {
  const redirect = useRedirect();
  const { watch } = useFormContext();
  const cycleId = watch("cycleId") || "";
  const hasValidCycleId = Number(cycleId) > 0;

  const handleCancel = () => {
    redirect("/cycles-logistics-send-history");
  };

  const handleSend = () => {
    if (!loading && hasValidCycleId && onSend) {
      onSend(cycleId);
    }
  };

  return (
    <S.StyledToolbar {...props}>
      <S.ToolbarCancelMuiButton onClick={handleCancel}>
        CANCELAR
      </S.ToolbarCancelMuiButton>
      <S.ToolbarSendMuiButton
        data-send-valid={hasValidCycleId ? "true" : "false"}
        disabled={!hasValidCycleId || loading}
        onClick={handleSend}
      >
        {loading ? <CircularProgress size={20} color="inherit" /> : "ENVIAR"}
      </S.ToolbarSendMuiButton>
    </S.StyledToolbar>
  );
};

const LuftExport = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const [loading, setLoading] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [pendingCycleId, setPendingCycleId] = useState("");

  const executeSend = async (cycleId) => {
    setLoading(true);
    try {
      const sendResult = await sendLuftExport({ cycleId, notify });

      if (sendResult.success) {
        redirect("/cycles-logistics-send-history");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseConfirmation = () => {
    if (!loading) {
      setIsConfirmationOpen(false);
    }
  };

  const handleConfirmSend = async () => {
    setIsConfirmationOpen(false);
    await executeSend(pendingCycleId);
  };

  const handleSend = async (cycleId) => {
    if (!cycleId || Number(cycleId) <= 0) {
      notify("Informe um número de ciclo válido.", { type: "warning" });
      return;
    }

    setLoading(true);
    try {
      const resendCheck = await checkLuftResend({ cycleId, notify });

      if (!resendCheck.canProceed) {
        return;
      }

      if (resendCheck.shouldConfirm) {
        setPendingCycleId(cycleId);
        setIsConfirmationOpen(true);
        return;
      }
    } finally {
      setLoading(false);
    }

    await executeSend(cycleId);
  };

  return (
    <>
      <Create>
        <SimpleForm
          onSubmit={(values) => handleSend(values?.cycleId)}
          toolbar={
            <CustomToolbar
              loading={loading}
              onSend={handleSend}
            />
          }
          sx={{ maxWidth: "100%", width: "100%" }}
        >
          <S.Container>
            <S.Header>
              <S.Title>Enviar dados de pedidos</S.Title>
              <S.Subtitle>
                Informe o ciclo para enviar as informações ao parceiro logístico.
              </S.Subtitle>
            </S.Header>

            <S.FormContainer>
              <TextInput
                source="cycleId"
                label="Ciclo"
                type="text"
                inputMode="numeric"
                parse={normalizeCycleId}
                fullWidth
              />
            </S.FormContainer>
          </S.Container>
        </SimpleForm>
      </Create>

      <ConfirmationModal
        open={isConfirmationOpen}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirmSend}
        title="Confirmar novo envio"
        message="Este ciclo já foi enviado anteriormente, deseja realizar novo envio? Dados poderão ser duplicados."
        confirmText="SEGUIR COM ENVIO"
        cancelText="NÃO ENVIAR"
      />
    </>
  );
};

export default LuftExport;
